import type { Registry } from '../content/registry';
import type { Biome, Terrain, Zone, ZoneName } from '../content/types';
import { fbm } from './noise';
import { hash01 } from './rng';
import { Settlements } from './settlements';
import { Overlay } from './overlay';

/** Continuous world fields. Every value is a pure function of (seed, x, y). */
export class Fields {
  private zones: Zone[];
  private biomes: Biome[];
  private biomeTotal: number;
  private terrainCache = new Map<string, Terrain>();
  readonly settlements: Settlements;
  /** the player-grown layer (plans and what was built from them); empty until a server / game loads one */
  readonly overlay = new Overlay();

  constructor(readonly seed: number, readonly reg: Registry) {
    this.zones = reg.zonesSorted().filter((z) => z.id !== 'water' && z.id !== 'park' && z.id !== 'industrial');
    this.biomes = reg.all('biome').filter((b) => b.weight > 0);
    if (this.biomes.length === 0) throw new Error('No biomes in content/biomes');
    this.biomeTotal = this.biomes.reduce((s, b) => s + b.weight, 0);
    this.settlements = new Settlements(seed, reg);
    this.seaLevel = Math.max(...this.biomes.map((b) => b.rules.find((r) => r.terrain === 'water')?.elevMax ?? 0), 0);
  }
  /** elevation below which a tile is water, everywhere */
  readonly seaLevel: number;

  /** Raw elevation noise (continuous). */
  elevationRaw(x: number, y: number): number { return fbm(this.seed + 11, x / 180, y / 180, 5); }
  /**
   * (perf, Sep 17 — docs/PERF.md) the raw elevation and its 5×5 mean, per tile but stored in 16×16 blocks of Float64Array (NaN =
   * not yet computed), each tile computed the first time it is asked for. `elevation()` used to box-filter 25 tiles through a
   * per-tile Map on every call — a chunk asks for every tile's elevation about five times, a cold chunk made 2.5 million lookups,
   * half of its time — and now the mean is kept too: one block lookup and an index after the first call. Still lazy per tile:
   * the path network and the places probe sample sparsely over kilometres, and computing whole blocks for them cost three times
   * what it saved (measured). The same doubles summed in the same order as before, so every tile is the same to the bit.
   */
  private blocks = new Map<number, { raw: Float64Array; mean: Float64Array }>();
  private static readonly B = 16;
  private block(bx: number, by: number): { raw: Float64Array; mean: Float64Array } {
    const k = (bx + 65536) * 131072 + (by + 65536);
    let b = this.blocks.get(k);
    if (!b) {
      if (this.blocks.size > 4096) this.blocks.clear(); // (a million tiles: the cap the tile Map had, times a few)
      const n = Fields.B * Fields.B;
      b = { raw: new Float64Array(n).fill(NaN), mean: new Float64Array(n).fill(NaN) }; this.blocks.set(k, b);
    }
    return b;
  }
  private elevTile(tx: number, ty: number): number {
    const B = Fields.B, bx = Math.floor(tx / B), by = Math.floor(ty / B), b = this.block(bx, by), i = (ty - by * B) * B + (tx - bx * B);
    let e = b.raw[i];
    if (e !== e) { e = this.elevationRaw(tx + 0.5, ty + 0.5); b.raw[i] = e; }
    return e;
  }
  /**
   * Elevation used for terrain: the raw noise box-filtered over 5×5 tiles, so shorelines are
   * clean lines instead of a speckle of one-tile sand islands and one-tile ponds (the high
   * noise octaves near the water threshold produced both). Cached per tile, the mean too.
   */
  elevation(x: number, y: number): number {
    const B = Fields.B, tx = Math.floor(x), ty = Math.floor(y), bx = Math.floor(tx / B), by = Math.floor(ty / B), b = this.block(bx, by), i = (ty - by * B) * B + (tx - bx * B);
    let m = b.mean[i];
    if (m !== m) {
      let s = 0;
      for (let dy = -2; dy <= 2; dy++) for (let dx = -2; dx <= 2; dx++) s += this.elevTile(tx + dx, ty + dy);
      m = s / 25; b.mean[i] = m;
    }
    return m;
  }
  moisture(x: number, y: number): number { return fbm(this.seed + 23, x / 140, y / 140, 3); }
  /** Urban density 0..1: 0 = wilderness, 1 = city core. */
  density(x: number, y: number): number {
    // irregular town outlines: warp the distance with low-frequency noise
    const n = fbm(this.seed + 37, x / 300, y / 300, 2);
    const base = this.settlements.density(x, y, 0.7 + 0.6 * n);
    return Math.min(1, Math.max(0, base));
  }
  flavor(x: number, y: number): number { return fbm(this.seed + 41, x / 120, y / 120, 2); }

  biomeAt(x: number, y: number): Biome {
    if (this.biomes.length === 1) return this.biomes[0];
    // per-tile jitter makes biome borders ragged instead of a hard line
    let r = (fbm(this.seed + 53, x / 900, y / 900, 2) + (this.speckle(x, y) - 0.5) * 0.06) * this.biomeTotal;
    for (const b of this.biomes) { r -= b.weight; if (r < 0) return b; }
    return this.biomes[this.biomes.length - 1];
  }

  /** `coarse`: one elevation sample instead of the 5×5 tile blur — for the world map, whose cells are tens of metres (25× cheaper, same terrain at that scale) */
  terrainAt(x: number, y: number, coarse = false): Terrain {
    const e = coarse ? this.elevTile(Math.floor(x), Math.floor(y)) : this.elevation(x, y), m = this.moisture(x, y);
    // water is decided by one sea level for the whole world (biomes may disagree about it,
    // and the ragged biome border would otherwise speckle every shoreline where two meet)
    if (e < this.seaLevel) return this.terrain('water');
    const biome = this.biomeAt(x, y);
    for (const r of biome.rules) {
      if (r.terrain === 'water') continue;
      if (r.elevMin !== undefined && e < r.elevMin) continue;
      if (r.elevMax !== undefined && e >= r.elevMax) continue;
      if (r.moistMin !== undefined && m < r.moistMin) continue;
      if (r.moistMax !== undefined && m >= r.moistMax) continue;
      const t = this.terrain(r.terrain);
      // wilderness between towns is wilder: damp grassland becomes forest
      if (t.id === 'grass' && m > 0.5 && this.reg.has('terrain', 'forest') && this.density(x, y) <= 0) return this.terrain('forest');
      return t;
    }
    return this.terrain(biome.rules[biome.rules.length - 1].terrain);
  }

  private terrain(id: string): Terrain {
    let t = this.terrainCache.get(id);
    if (!t) { t = this.reg.need('terrain', id); this.terrainCache.set(id, t); }
    return t;
  }

  /** Zone ignoring water (roads/lots use this; water is handled per tile). */
  urbanZone(x: number, y: number): ZoneName {
    const d = this.density(x, y);
    let z: ZoneName = this.zones[0]?.id ?? 'rural';
    for (const zone of this.zones) if (d >= zone.densityMin) z = zone.id;
    if (z !== 'rural') {
      const f = this.flavor(x, y);
      if (f > 0.64 && this.reg.has('zone', 'industrial')) return 'industrial';
      if (f < 0.2 && this.reg.has('zone', 'park')) return 'park';
    }
    return z;
  }

  zoneAt(x: number, y: number): ZoneName {
    const t = this.terrainAt(x, y);
    if (!t.walkable && !t.buildable) return 'water';
    return this.overlay.zoneAt(x, y) ?? this.urbanZone(x, y);
  }
  /** Wilderness: nothing generates here but corridors — the part of the map players may grow into. */
  wild(x: number, y: number): boolean { return this.urbanZone(x, y) === 'rural' && this.density(x, y) < 0.02; }

  zone(name: ZoneName): Zone {
    return this.reg.need('zone', name);
  }

  /** Per-tile noise for texture (0..1). */
  speckle(x: number, y: number): number { return hash01(this.seed + 7, x, y); }
}
